import { ElementRef, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { AssertService } from '@connect-services/client/services/assert.service';
import { WidgetConfig } from '@connect-services/src/sdk/api';

const BTN_ID = 'cq-thread-history-btn';

export type Thread = {
    createdAt: string;
    id: string;
    metadata: { cs_project_id: string };
    name: string;
};

type FetchConnieHistoryState = {
    cursor: string | null;
    loading: boolean;
    search?: string;
};

@Injectable({
    providedIn: 'root',
})
export class ConnieUiService {
    public readonly isHistoryOpen = signal(false);
    public readonly panelRef: WritableSignal<ElementRef<HTMLElement> | undefined> = signal(undefined);
    public readonly state = signal<FetchConnieHistoryState>({
        cursor: null,
        loading: false,
        search: '',
    });
    public readonly threads = signal<Thread[]>([]);

    private readonly translocoService = inject(TranslocoService);

    // ——— Highlight helpers (unchanged) ———
    public attachGraphEditHighlight(shadow: ShadowRoot): void {
        if (shadow.__graphHighlightAttached) {
            return;
        }

        shadow.__graphHighlightAttached = true;

        let highlightActive = false;
        const getInputContainer = (): HTMLElement | null => {
            const input = shadow.getElementById('chat-input');

            return input ? input.closest('.bg-accent') : null;
        };

        const addHighlight = (): void => {
            const inputContainer = getInputContainer();

            if (!inputContainer) {
                return;
            }

            inputContainer.classList.add('highlight-input');
            highlightActive = true;
        };

        const removeHighlight = (): void => {
            const inputContainer = getInputContainer();

            if (inputContainer) {
                inputContainer.classList.remove('highlight-input');
            }

            highlightActive = false;
        };

        const wireActionButtons = (): void => {
            const buttons: NodeListOf<HTMLElement> = shadow.querySelectorAll(
                '#modify_graph_action_button, #generate_graph_action_button',
            );

            buttons.forEach((button: HTMLElement) => {
                if (button.__hlAttached) {
                    return;
                }

                button.addEventListener('click', addHighlight);
                button.__hlAttached = true;
            });
        };

        const wireSendButton = (): void => {
            const send = shadow.getElementById('chat-submit');
            if (send && !send.__hlAttached) {
                send.addEventListener('click', () => {
                    if (!highlightActive) {
                        return;
                    }

                    setTimeout(removeHighlight, 20);
                });
                send.__hlAttached = true;
            }
        };

        if (!shadow.__hlKeydown) {
            shadow.addEventListener(
                'keydown',
                (event: Event) => {
                    AssertService.hasKey(event, 'key');
                    if (event['key'] !== 'Enter' || !highlightActive) {
                        return;
                    }

                    setTimeout(removeHighlight, 20);
                },
                true,
            );
            shadow.__hlKeydown = true;
        }

        wireActionButtons();
        wireSendButton();

        if (shadow.__hlObserver) {
            return;
        }

        const mutationObserver = new MutationObserver(() => {
            wireActionButtons();
            wireSendButton();
        });
        mutationObserver.observe(shadow, { childList: true, subtree: true });
        shadow.__hlObserver = mutationObserver;
    }

    // ——— Layout setup ———
    public centerPopoverInShadow(shadow: ShadowRoot): void {
        this.recenterInline(shadow);
        this.normalizeMessageWidths(shadow);
        this.pinContentWidthEarly(shadow);
    }

    public clearChainlitContext(): void {
        if (!window.__connieMounted) {
            return;
        }

        window.unmountChainlitWidget?.();
        window.__cq_chainlit_cfg = null;
        window.currentProjectId = '';
        window.currentSessionId = '';
        window.currentUserId = '';
        window.currentUserName = '';
        window.__chainlitBundleLoaded = false;
        window.__connieMounted = false;
        window.__cq_authFetchInstalled = false;
    }

    public clearSignals(): void {
        this.isHistoryOpen.set(false);
        this.panelRef.set(undefined);
        this.threads.set([]);
        this.state.set({ cursor: null, loading: false, search: '' });
    }

    public enhanceChatUI(shadow: ShadowRoot): void {
        this.injectHeaderHiderCSS(shadow);
        this.hideFloatingButtonInShadow(shadow);
        this.hideChatSettingsButtonInShadow(shadow);
        this.centerPopoverInShadow(shadow);
        this.hideMaximizeButtonInShadow(shadow);
        this.attachGraphEditHighlight(shadow);
        this.boot();
    }

    // ——— Message + composer width (scoped) ———
    public ensureContentWidthCSS(shadow: ShadowRoot): void {
        let shadowStyle = shadow.getElementById('cq-content-width-style');

        if (!shadowStyle) {
            shadowStyle = document.createElement('style');
            shadowStyle.id = 'cq-content-width-style';
            shadow.appendChild(shadowStyle);
        }

        shadowStyle.textContent = `
        #chainlit-copilot-chat {
            background-color: var(--on-primary) !important;
            border: 0px solid var(--on-primary) !important;
        }

        /* Message list containers */
        #chainlit-copilot-chat .cq-dialog:not(.cq-fullscreen) [data-testid="messages-container"],
        #chainlit-copilot-chat .cq-dialog:not(.cq-fullscreen) .cl-message-list {
          max-width: var(--cq-content-max) !important;
          width: 100% !important;
          margin-left: auto !important;
          margin-right: auto !important;
          padding-inline: var(--cq-content-pad) !important;
          box-sizing: border-box !important;
          text-align: left !important;
        }

        /* Composer row (align with messages) */
        #chainlit-copilot-chat .cq-dialog:not(.cq-fullscreen) .mx-auto.w-full.px-4.pb-4 {
          max-width: var(--cq-content-max) !important;
          margin-left: auto !important;
          margin-right: auto !important;
          padding-inline: var(--cq-content-pad) !important;
          box-sizing: border-box !important;
        }

        /* Stretch bubbles but keep layout intact */
        #chainlit-copilot-chat .cq-dialog:not(.cq-fullscreen) [data-testid="messages-container"] .cl-message,
        #chainlit-copilot-chat .cq-dialog:not(.cq-fullscreen) [data-testid="messages-container"] [data-testid="message"] {
          max-width: none !important;
          width: 100% !important;
          text-align: left !important;
        }

        /* Neutralize width caps only inside messages */
        #chainlit-copilot-chat .cq-dialog:not(.cq-fullscreen) [data-testid="messages-container"] .prose,
        #chainlit-copilot-chat .cq-dialog:not(.cq-fullscreen) [data-testid="messages-container"] [class*="max-w-"] {
          max-width: var(--cq-content-max) !important;
          width: 100% !important;
          text-align: left !important;
        }

        /* Remove accidental center text utilities inside messages only */
        #chainlit-copilot-chat .cq-dialog:not(.cq-fullscreen) [data-testid="messages-container"] .text-center { text-align: left !important; }

        #chainlit-copilot-chat [data-step-type="user_message"] > div > div > div > div > div > div {
            background-color: var(--custom-light-blue);
            color: var(--custom-blue);
            border-radius: 0.5rem;
        }

        [data-radix-popper-content-wrapper] > div {
            border: 0;
            box-shadow: 0 0 #0000;
        }

        [data-step-type="assistant_message"] > [data-radix-popper-content-wrapper] {
            z-index: 2147483647 !important;
        }

        .ai-message > div {
            align-items: flex-end !important;
        }

        #new-chat-button {
            background-color: var(--custom-blue);
            color: var(--on-primary);
        }

        a:hover {
            background-color: var(--custom-blue) !important;
            color: var(--on-primary) !important;
        }

        #message-composer {
            background-color: var(--on-primary);
            box-shadow: 0 0 10px 2px rgb(0 0 0 / 0.1);
        }

        .mapboxgl-ctrl-bottom-right > div {
            position: relative;
            display: flex;
        }

        #chat-submit {
            background-color: var(--custom-blue);
        }

        #stop-button {
            background-color: var(--custom-blue);
        }

        #chat-settings-open-modal {
            color: var(--custom-blue);
        }

        #chainlit-copilot-chat > div:nth-of-type(2) > div > div > div:nth-of-type(1) > div > div {
            max-width: none !important;
        }

        [data-step-type="user_message"] {
            display: flex;
            justify-content: end;
            max-width: 100% !important;

            textarea {
                background-color: var(--custom-light-blue);
                color: var(--custom-blue);
            }

            button:nth-of-type(1) {
                background-color: var(--on-primary);
            }

            button:nth-of-type(2) {
                background-color: var(--custom-blue);
            }

            div > * {
                max-width: none !important;
            }
        }

        /* =====================  Copilot welcome (first assistant message) ===================== */

        /* Hide avatar so we can center the block */
        .step[data-step-type="assistant_message"]:first-of-type .ai-message > span:first-child {
          display: none !important;
        }

        /* Center the whole message */
        .step:first-of-type[data-step-type="assistant_message"] {
          display: flex !important;
          justify-content: center !important;
          .ai-message {
            gap: 0 !important;
          }
        }
        .step:first-of-type[data-step-type="assistant_message"] .message-content {
          max-width: 720px !important;
          margin: 0 auto !important;
          text-align: center !important;
          align-items: center !important;
        }

        /* Fix the image wrapper: remove ratio AND absolute positioning */
        .step[data-step-type="assistant_message"]:first-of-type [data-radix-aspect-ratio-wrapper] {
          width: auto !important;
          padding-bottom: 0 !important;
          height: auto !important;
        }
        .step[data-step-type="assistant_message"]:first-of-type [data-radix-aspect-ratio-wrapper] > .bg-muted {
          position: static !important;
          inset: auto !important;
          background: transparent !important;
          overflow: visible !important;
          display: inline-block !important;
        }

        /* Size the Connie logo */
        .step:first-of-type[data-step-type="assistant_message"] img[alt="Connie"] {
          display: block !important;
          width: 140px !important;
          height: auto !important;
          margin: 12px auto 16px !important;
          object-fit: contain !important;
          background: transparent !important;
        }

        /* Center the “Questions de découverte” row */
        .step[data-step-type="assistant_message"]:first-of-type .flex.items-center.flex-wrap {
          width: 100% !important;
          margin-left: 0 !important;
          justify-content: center !important;
          align-items: center !important;
          gap: 0.25rem !important;
        }

        /* Keep the button shrink-to-content and centered */
        .step[data-step-type="assistant_message"]:first-of-type .flex.items-center.flex-wrap > button {
          display: inline-flex !important;
          width: auto !important;
          margin: 0 auto !important;
        }

        /* =====================  Copilot widget CSS (Shadow DOM)  ===================== */
        /* 1) Bring Mapbox GL CSS *inside* the widget */
        @import url("https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css");


        /* =====================  Plotly/Mapbox layering fixes  ===================== */
        /* Keep Plotly boxes from clipping overlay layers */
        #chainlit-copilot-chat .js-plotly-plot,
        #chainlit-copilot-chat .plot-container,
        #chainlit-copilot-chat .svg-container,
        #chainlit-copilot-chat .main-svg {
          overflow: visible !important;
        }

        /* We place overlay SVGs absolutely, but we keep the *first* one in normal flow.
           Plotly usually renders multiple sibling <svg.main-svg>. The first svg holds the
           paper/background (your black bg); the next ones contain titles/legend/hover. */
        #chainlit-copilot-chat .svg-container {
          position: relative !important;
          overflow: visible !important;
        }

        /* FIRST svg = paper/background → keep it in flow so its background is visible */
        #chainlit-copilot-chat .svg-container > svg.main-svg:first-of-type {
          position: relative !important;
          z-index: 0 !important;
          display: block !important;
          overflow: visible !important;
        }

        /* Any subsequent svg(s) = overlays (titles/legend/hover) → stack above & don’t intercept events */
        #chainlit-copilot-chat .svg-container > svg.main-svg:not(:first-of-type) {
          position: absolute !important;
          inset: 0 !important;
          z-index: 20 !important;
          overflow: visible !important;
          background: transparent !important;   /* overlays must not hide the paper */
          pointer-events: none !important;      /* overlays won't block map/plot */
        }

        /* Legend must remain clickable even if its parent svg is pointer-events:none */
        #chainlit-copilot-chat .svg-container > svg.main-svg:not(:first-of-type) .legend {
          pointer-events: auto !important;
          z-index: 40 !important;
        }

        /* Hover tooltips should never capture the mouse */
        #chainlit-copilot-chat .hoverlayer {
          pointer-events: none !important;
          z-index: 50 !important;
        }

        /* WebGL/Mapbox canvas sits between paper and overlays */
        #chainlit-copilot-chat .gl-container,
        #chainlit-copilot-chat .mapboxgl-canvas-container,
        #chainlit-copilot-chat .mapboxgl-canvas {
          pointer-events: auto !important;
          z-index: 10 !important;
        }

        /* Plotly modebar: keep it clickable but don’t block the plot underneath */
        #chainlit-copilot-chat .modebar-container {
          pointer-events: none !important;     /* container lets events pass through */
          position: absolute !important;
          top: 4px !important;
          right: 1px !important;
          width: auto !important;
          z-index: 80 !important;
        }
        #chainlit-copilot-chat .modebar-container .modebar {
          pointer-events: auto !important;     /* buttons remain interactive */
          border-radius: 8px !important;
          box-shadow: 0 4px 12px rgba(0,0,0,.25) !important;
        }

        /* Semi-transparent modebar background (readable over content) */
        :host-context(.dark) #chainlit-copilot-chat .modebar {
          background: rgba(0, 0, 0, 0.50) !important;
        }
        :host-context(:not(.dark)) #chainlit-copilot-chat .modebar {
          background: rgba(255, 255, 255, 0.50) !important;
          border: 1px solid rgba(0,0,0,0.08) !important;
        }

        /* Modebar icon colors + hovers (light/dark) */
        :host-context(.dark) #chainlit-copilot-chat .modebar-btn {
          color: rgba(255,255,255,0.9) !important;
        }
        :host-context(.dark) #chainlit-copilot-chat .modebar-btn:hover,
        :host-context(.dark) #chainlit-copilot-chat .modebar-btn.active {
          background: rgba(255,255,255,0.12) !important;
          color: #fff !important;
        }
        :host-context(:not(.dark)) #chainlit-copilot-chat .modebar-btn {
          color: rgba(0,0,0,0.72) !important;
        }
        :host-context(:not(.dark)) #chainlit-copilot-chat .modebar-btn:hover,
        :host-context(:not(.dark)) #chainlit-copilot-chat .modebar-btn.active {
          background: rgba(0,0,0,0.06) !important;
          color: rgba(0,0,0,0.86) !important;
        }

        /* Prevent clipping from the outer inline Plotly wrapper if it sets overflow:hidden */
        #chainlit-copilot-chat .inline-plotly.js-plotly-plot {
          overflow: visible !important;
        }

        /* Ensure cartesian charts (bar/pie/line…) receive mouse events for hover */
        #chainlit-copilot-chat .svg-container > svg.main-svg:first-of-type .draglayer,
        #chainlit-copilot-chat .svg-container > svg.main-svg:first-of-type .cartesianlayer,
        #chainlit-copilot-chat .svg-container > svg.main-svg:first-of-type .cartesianlayer .plot,
        #chainlit-copilot-chat .svg-container > svg.main-svg:first-of-type .cartesianlayer .barlayer *,
        #chainlit-copilot-chat .svg-container > svg.main-svg:first-of-type .cartesianlayer .scatterlayer *,
        #chainlit-copilot-chat .svg-container > svg.main-svg:first-of-type .cartesianlayer .pielayer * {
          pointer-events: all !important;
          cursor: crosshair; /* optional: mimic Plotly’s hover cursor */
        }


        /* =====================  UI theming  ===================== */
        /* Brand tokens (scoped to the Shadow Root) */
        :host {
          --brand-start: #E41EDF;
          --brand-end:   #40B0F5;
          --brand-fallback: #8D6CEB;
          --brand-outline:  #8D6CEB;
          --brand-shadow:   rgba(64,176,245,0.35);
          --brand-text-on:  #FFFFFF;
          --brand-text-soft:#F8F9FF;
        }

        /* Hide watermark inside the widget */
        .watermark { display: none !important; }

        /* Round inner elements in the composer */
        #message-composer, #chat-input { border-radius: 12px !important; }

        .ai-message > div > div:nth-of-type(2) {
            padding-top: 1rem;

            button:not(#modify_graph_action_button):hover {
                 background: var(--custom-blue) !important;
                 border-color: var(--custom-blue) !important;
                 color: var(--on-primary) !important;
                 opacity: 0.96;
            }

            div > *:hover {
                background: var(--custom-blue) !important;
                border-color: var(--custom-blue) !important;
                color: var(--on-primary) !important;
                opacity: 0.96;
            }
        }

        /* Primary action buttons (subtle / darker pills) */
        #modify_graph_action_button,
        #generate_graph_action_button {
          background: var(--custom-light-blue) !important;
          color: var(--custom-blue) !important;

          padding: 0.45rem 0.8rem !important;
          border-radius: 0.75rem !important;
          font-weight: 500 !important;
          font-size: 0.9rem !important;
          margin-left: 0.35rem;
          cursor: pointer;

          box-shadow: none !important;
          text-shadow: none !important;
          transition: background-color .15s ease, border-color .15s ease, color .15s ease, opacity .15s ease;
        }
        #modify_graph_action_button:hover,
        #modify_graph_action_button:focus-visible,
        #generate_graph_action_button:hover,
        #generate_graph_action_button:focus-visible {
          background: var(--custom-blue) !important;
          border-color: var(--custom-blue) !important;
          color: var(--on-primary) !important;
          opacity: 0.96;
        }
        #modify_graph_action_button:active,
        #generate_graph_action_button:active {
          background: hsl(var(--primary) / 0.18) !important;
          border-color: hsl(var(--primary) / 0.45) !important;
        }
        :host-context(.dark) #modify_graph_action_button,
        :host-context(.dark) #generate_graph_action_button {
          background: hsl(var(--primary) / 0.10) !important;
          border-color: hsl(var(--primary) / 0.24) !important;
          color: hsl(var(--foreground) / 0.75) !important;
        }
        :host-context(.dark) #modify_graph_action_button:hover,
        :host-context(.dark) #modify_graph_action_button:focus-visible,
        :host-context(.dark) #generate_graph_action_button:hover,
        :host-context(.dark) #generate_graph_action_button:focus-visible {
          background: hsl(var(--primary) / 0.16) !important;
          border-color: hsl(var(--primary) / 0.40) !important;
          color: hsl(var(--foreground) / 0.80) !important;
        }

        /* Red outline while editing the input → brand look */
        .bg-accent.highlight-input {
          box-sizing: border-box !important;
          border: 4px solid var(--brand-outline) !important;
          border-radius: 15px !important;
          padding: 2px !important;
          box-shadow: 0 0 0 3px var(--brand-shadow) !important;
        }

        /* User bubble: gentle shadow in both themes */
        [data-testid="chat-message"] .ml-auto .rounded-2xl {
          box-shadow: 0 6px 14px rgba(0,0,0,.10) !important;
        }
        :host-context(.dark) [data-testid="chat-message"] .ml-auto .rounded-2xl {
          box-shadow: 0 10px 24px rgba(0,0,0,.28) !important;
        }

        /* Reorder/flow adjustments */
        .-ml-1\\.5.flex.items-center.flex-wrap {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 0.5rem;
        }
        .-ml-1\\.5.flex.items-center.flex-wrap > * { order: 1; }
        .-ml-1\\.5.flex.items-center.flex-wrap button:has(svg.lucide-copy) { order: 99 !important; }
        .-ml-1\\.5.flex.items-center.flex-wrap .flex.items-center { order: 99 !important; }
        .-ml-1\\.5.flex.items-center.flex-wrap button[class*="inline-flex"]:not([class*="w-9"]) {
          white-space: normal !important;
          word-wrap: break-word;
          overflow-wrap: anywhere;
          height: auto !important;
          max-width: min(48rem, 100vw);
          padding: 0.5rem 1rem !important;
          overflow: visible !important;
          flex: 1 0 100%;
          width: 100%;
        }

        /* CSV download under DataFrame */
        .flex.flex-col.gap-4 > .flex.gap-1 { order: 1 !important; }
        .flex.flex-col.gap-4 > .flex.items-center.gap-2 { order: 2 !important; }

        /* DataFrame pagination scrolling */
        ul.flex.ml-auto {
          flex-wrap: nowrap;
          overflow-x: auto;
          max-width: 100%;
          padding-bottom: 4px;
          scrollbar-width: thin;
        }
        ul.flex.ml-auto::-webkit-scrollbar { height: 6px; }
        ul.flex.ml-auto::-webkit-scrollbar-thumb { background: #555; border-radius: 3px; }

        /* Hide maximize/minimize button in header */
        #chainlit-copilot-chat button:has(svg.lucide.lucide-maximize),
        #chainlit-copilot-chat svg.lucide.lucide-maximize {
          display: none !important;
          pointer-events: none !important;
        }

        /* Extra safety: hide floating open button if ever re-inserted */
        #chainlit-copilot-button { display: none !important; }

        /* ====== History UI for Copilot ====== */
        .cq-history-trigger {
          margin-left: 8px;
          border: none;
          font-size: 0.875rem;
          line-height: 1.25rem;
          background-color: var(--custom-blue);
          cursor: pointer;
          padding: 12px 8px;
          color: white;
          border-radius: 0.75rem;
        }

        .cq-history-trigger:hover {
          background-color: var(--custom-light-blue);
          color: var(--custom-blue);
        }
    `;
    }

    public getShadowRoot(): ShadowRoot | null {
        const host = document.getElementById('chainlit-copilot');
        if (!host) {
            return null;
        }

        return host.shadowRoot;
    }

    // ——— Desktop centering (inline, no layout mutations) ———
    public getVisibleWrapper(shadow: ShadowRoot): HTMLElement | undefined {
        const wrappers: HTMLElement[] = Array.from(shadow.querySelectorAll('[data-radix-popper-content-wrapper]'));

        return (
            wrappers.find((wrapper: HTMLElement) => wrapper.offsetWidth > 0 || wrapper.offsetHeight > 0) ||
            wrappers[0] ||
            null
        );
    }

    public hideChatSettingsButtonInShadow(shadow: ShadowRoot): void {
        const button: HTMLElement | null = shadow.querySelector('#chat-settings-open-modal');

        if (!button) {
            return;
        }

        button.style.display = 'none';
    }

    public hideFloatingButtonInShadow(shadow: ShadowRoot): void {
        const button: HTMLElement | null = shadow.querySelector('#chainlit-copilot-button');

        if (!button) {
            return;
        }

        button.style.display = 'none';
    }

    public hideMaximizeButtonInShadow(shadow: ShadowRoot): void {
        const icon = shadow.querySelector('#chainlit-copilot-chat svg.lucide.lucide-maximize');
        const buttonByIcon = icon ? icon.closest('button') : null;

        if (buttonByIcon) {
            buttonByIcon.style.display = 'none';
        }

        const newButton = shadow.getElementById('new-chat-button');
        const sibling: HTMLElement | null = newButton?.parentElement?.nextElementSibling as HTMLElement | null;

        if (sibling?.tagName !== 'BUTTON') {
            return;
        }

        sibling.style.display = 'none';
    }

    // ——— Small header tweak ———
    public injectHeaderHiderCSS(shadow: ShadowRoot): void {
        if (shadow.getElementById('hide-maximize-style')) {
            return;
        }

        const style = document.createElement('style');
        style.id = 'hide-maximize-style';
        style.textContent = `
            #chainlit-copilot-chat button:has(svg.lucide.lucide-maximize) { display: none !important; }
            #chainlit-copilot-chat svg.lucide.lucide-maximize { display: none !important; pointer-events: none !important; }
      `;
        shadow.appendChild(style);
    }

    public normalizeMessageWidths(shadow: ShadowRoot): void {
        this.ensureContentWidthCSS(shadow);

        const msgScope: HTMLElement | null =
            shadow.querySelector(
                '#chainlit-copilot-chat .cq-dialog:not(.cq-fullscreen) [data-testid="messages-container"]',
            ) || shadow.querySelector('#chainlit-copilot-chat .cq-dialog:not(.cq-fullscreen) .cl-message-list');

        if (!msgScope) {
            return;
        }

        // Remove center utilities inside messages
        (msgScope.querySelectorAll('.text-center') as NodeListOf<HTMLElement>).forEach((element: HTMLElement) => {
            element.classList.remove('text-center');
            element.style.textAlign = 'left';
        });

        // Inline caps sometimes injected during streaming
        (
            msgScope.querySelectorAll(
                '[style*="max-width"], .prose, [class*="max-w-"], .cl-message, [data-testid="message"]',
            ) as NodeListOf<HTMLElement>
        ).forEach((element: HTMLElement) => {
            if (element.style?.maxWidth) {
                element.style.maxWidth = 'var(--cq-content-max)';
            }

            element.style.width = '100%';
            element.style.textAlign = 'left';
        });
    }

    // ——— Observe and keep things centered/wide as content streams ———
    public observeShadow(shadow: ShadowRoot): void {
        if (shadow.__copilotObserver) {
            return;
        }

        const lightEnhance = (): void => {
            this.injectHeaderHiderCSS(shadow);
            this.hideFloatingButtonInShadow(shadow);
            this.hideChatSettingsButtonInShadow(shadow);
            this.hideMaximizeButtonInShadow(shadow);
            this.attachGraphEditHighlight(shadow);
            this.recenterInline(shadow);
            this.normalizeMessageWidths(shadow); // scoped to messages only
            this.manageModalStyle(shadow);
            this.boot();
        };

        let requestAnimationFrameValue: number | null = null;
        const schedule = (): void => {
            if (requestAnimationFrameValue) {
                return;
            }

            requestAnimationFrameValue = requestAnimationFrame(() => {
                requestAnimationFrameValue = null;
                lightEnhance();
            });
        };

        const root = shadow.getElementById('chainlit-copilot-chat') || shadow;
        const mutationObserver = new MutationObserver(schedule);
        mutationObserver.observe(root, { characterData: true, childList: true, subtree: true });
        shadow.__copilotObserver = mutationObserver;

        // Recenter & re-widen on dialog resize
        if (!shadow.__cqResizeObs) {
            const wrapper = this.getVisibleWrapper(shadow);
            const dialog = wrapper?.querySelector('[role="dialog"]') || wrapper;
            if (dialog && 'ResizeObserver' in window) {
                const ro = new ResizeObserver(() => {
                    this.recenterInline(shadow);
                    this.normalizeMessageWidths(shadow);
                });
                ro.observe(dialog);
                shadow.__cqResizeObs = ro;
            }
        }

        // First-open stabilization
        lightEnhance();
        requestAnimationFrame(() => {
            this.recenterInline(shadow);
            this.normalizeMessageWidths(shadow);
        });
        setTimeout(() => {
            this.recenterInline(shadow);
            this.normalizeMessageWidths(shadow);
        }, 250);
        document.fonts?.ready?.then(() => {
            this.recenterInline(shadow);
            this.normalizeMessageWidths(shadow);
        });
    }

    // Pin a few frames during first paint/stream
    public pinContentWidthEarly(shadow: ShadowRoot): void {
        let frames = 0;

        const tick = (): void => {
            this.normalizeMessageWidths(shadow);
            this.recenterInline(shadow);

            if (++frames >= 20) {
                return;
            }

            requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
        document.fonts?.ready?.then(() => this.normalizeMessageWidths(shadow));
    }

    public recenterInline(shadow: ShadowRoot): void {
        // ~30% wider than before: 1200px → 1560px
        const wrapper = this.getVisibleWrapper(shadow);

        if (!wrapper) {
            return;
        }

        if (wrapper.classList.contains('cq-fullscreen')) {
            return;
        }

        wrapper.style.setProperty('position', 'relative', 'important');
        wrapper.style.setProperty('left', '50%', 'important');
        wrapper.style.setProperty('top', '22rem', 'important');
        wrapper.style.setProperty('right', '0', 'important');
        wrapper.style.setProperty('bottom', '0', 'important');
        wrapper.style.setProperty('transform', 'translate(-50%, -50%)', 'important');

        const dialog: HTMLElement | null = wrapper.querySelector('[role="dialog"]');

        if (dialog) {
            dialog.style.setProperty('width', '100%', 'important');
            dialog.style.setProperty('height', '44rem', 'important');
            dialog.style.setProperty('border-radius', '0px', 'important');
            dialog.style.setProperty('overflow', 'hidden', 'important');
        }

        const child: HTMLElement | null = wrapper.firstElementChild as HTMLElement | null;

        if (!child?.style?.transform) {
            return;
        }

        child.style.setProperty('transform', 'none', 'important');
    }

    public async setThreadHistory(): Promise<void> {
        const widgetConfig = window.__cq_chainlit_cfg;

        if (!widgetConfig) {
            return;
        }

        const data: Thread[] = await this.fetchThreadHistory(widgetConfig);

        const projectThreads = data.filter((thread) => thread.metadata.cs_project_id === window.currentProjectId);
        this.threads.set(
            projectThreads.sort(
                (threadA, threadB) => new Date(threadB.createdAt).getTime() - new Date(threadA.createdAt).getTime(),
            ),
        );

        this.state.set({ ...this.state(), loading: false });
    }

    private boot(): void {
        const shadow = this.getShadowRoot();
        if (!shadow) {
            return;
        }

        const header = this.findHeader(shadow);
        if (!header) {
            return;
        }

        this.ensureHistoryButtonInHeader(header);
    }

    private ensureHistoryButtonInHeader(container: HTMLElement): void {
        if (!container || container.querySelector(`#${BTN_ID}`)) {
            return;
        }

        const btn = document.createElement('button');
        btn.id = BTN_ID;
        btn.className = 'cq-history-trigger';
        btn.textContent = `📚 ${this.translocoService.translate('chatHistory')}`;
        btn.addEventListener('click', async () => {
            this.isHistoryOpen.set(!this.isHistoryOpen());

            const shadowRoot = this.getShadowRoot();

            if (!shadowRoot) {
                return;
            }

            this.enhanceChatUI(shadowRoot);

            if (this.isHistoryOpen()) {
                await this.setThreadHistory();

                return;
            }

            this.state.set({ ...this.state(), cursor: null });
            this.threads.set([]);
        });
        container.appendChild(btn);
    }

    private async fetchThreadHistory(widgetConfig: WidgetConfig): Promise<Thread[]> {
        this.state.set({ ...this.state(), loading: true });

        const body = {
            filter: {
                feedback: null,
                search: this.state().search || '',
                userId: window.currentUserId || null, // server still enforces identity
            },
            pagination: { cursor: this.state().cursor, first: 0 },
        };

        const res = await fetch(`${widgetConfig.chainlitServer}/project/threads`, {
            body: JSON.stringify(body),
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
        });

        if (!res.ok) {
            throw new Error(await res.text());
        }

        const json = await res.json();

        const pageInfo = json.pageInfo;

        this.state.set({
            ...this.state(),
            cursor: pageInfo ? pageInfo.endCursor : null,
        });

        return json.data;
    }

    private findHeader(shadow?: ShadowRoot): HTMLElement | null {
        if (!shadow) {
            return null;
        }
        const root = shadow.querySelector('chainlit-copilot-chat') || shadow;

        // Try common header rows
        const header: HTMLElement | null =
            root.querySelector('#chainlit-copilot-chat header') ||
            root.querySelector('header') ||
            root.querySelector('.p-4.pb-0') ||
            root.querySelector('.flex.align-center.justify-between.p-4.pb-0');

        if (!header) {
            return null;
        }

        return header.querySelector('.flex.items-center.gap-1') || header;
    }

    private manageModalStyle(shadow: ShadowRoot): void {
        const modal = shadow.getElementById('new-chat-dialog');
        const confirmButton = shadow.getElementById('confirm');

        const submitButton = shadow.getElementById('submit-feedback');

        if (submitButton) {
            submitButton.style.backgroundColor = 'var(--custom-blue)';
            const modalParent = submitButton.parentElement!.parentElement!;

            modalParent.style.zIndex = '2147483647';
        }

        if (modal || submitButton) {
            const root = shadow.getElementById('cl-shadow-root');
            const modalBackground: HTMLElement = (Array.from(root?.children || []) as HTMLElement[]).filter(
                (el: HTMLElement) => el.tagName === 'DIV',
            )[1];

            modalBackground.style.background = 'transparent';
            modalBackground.style.zIndex = '9999';

            modalBackground.addEventListener('click', (event) => {
                event.stopPropagation();
            });

            modalBackground.addEventListener('mousedown', (event) => {
                event.stopPropagation();
            });

            modalBackground.addEventListener('pointerdown', (event) => {
                event.stopPropagation();
            });
        }

        if (modal) {
            modal.style.zIndex = '2147483647';
        }

        if (confirmButton) {
            confirmButton.style.backgroundColor = 'var(--custom-blue)';
        }
    }
}
